import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import Card from '../../ui/Card'
import Button from '../../ui/Button'
import SearchInput from '../../ui/SearchInput'
import CenteredState from '../../ui/CenteredState'
import Toast from '../../shared/Toast'
import Loader from '../../shared/Loader'
import useIssueStatuses from '../hooks/useIssueStatuses'
import StatusesTable from './StatusesTable'
import EditStatusesDrawer from './EditStatusesDrawer'

const TOAST_CONTAINER_ID = 'issue-statuses-toast'
const SEARCH_DEBOUNCE_MS = 300

const IssueStatusesTab = ({ collectId, palette }) => {
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [hasOpenedEdit, setHasOpenedEdit] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [sort, setSort] = useState(null)
  const [direction, setDirection] = useState('asc')

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchInput), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(id)
  }, [searchInput])

  const { statuses, loading, hasLoaded, error, refetch } = useIssueStatuses(
    collectId, { search: debouncedSearch, sort, direction }
  )
  const {
    statuses: allStatuses, hasLoaded: allHasLoaded, refetch: refetchAll
  } = useIssueStatuses(hasOpenedEdit ? collectId : null)

  const openEdit = useCallback(() => {
    setHasOpenedEdit(true)
    setIsEditOpen(true)
  }, [])
  const closeEdit = useCallback(() => setIsEditOpen(false), [])
  const handleSearchChange = useCallback(event => setSearchInput(event.target.value), [])

  const handleSortChange = useCallback((nextSort, nextDirection) => {
    setSort(nextSort)
    setDirection(nextDirection)
  }, [])

  const handleSaved = useCallback(() => {
    refetch()
    refetchAll()
  }, [refetch, refetchAll])

  if (loading && !hasLoaded) {
    return (
      <div>
        <Toast containerId={TOAST_CONTAINER_ID} />
        <CenteredState><Loader /></CenteredState>
      </div>
    )
  }

  const drawerLoading = hasOpenedEdit && !allHasLoaded

  const editButton = (
    <Button disabled={loading} icon="gear" variant="neutral" onClick={openEdit}>
      Modifier des statuts
    </Button>
  )

  return (
    <div>
      <Toast containerId={TOAST_CONTAINER_ID} />
      <div className="tw:flex tw:items-center tw:justify-between tw:gap-3 tw:mb-4">
        <div className="tw:w-full tw:max-w-[531px]">
          <SearchInput
            name="issue-statuses-search"
            placeholder="Rechercher un statut..."
            value={searchInput}
            onChange={handleSearchChange}
          />
        </div>
        {editButton}
      </div>
      <Card title="Statuts configurés" plainHeader>
        <StatusesTable
          direction={direction}
          error={error}
          loading={loading}
          searching={Boolean(debouncedSearch)}
          sort={sort}
          statuses={statuses}
          onSortChange={handleSortChange}
        />
      </Card>
      <EditStatusesDrawer
        collectId={collectId}
        isOpen={isEditOpen}
        loading={drawerLoading}
        palette={palette}
        statuses={allStatuses}
        toastContainerId={TOAST_CONTAINER_ID}
        onClose={closeEdit}
        onSaved={handleSaved}
      />
    </div>
  )
}

IssueStatusesTab.propTypes = {
  collectId: PropTypes.number.isRequired,
  palette: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string))
}

IssueStatusesTab.defaultProps = {
  palette: {}
}

export default IssueStatusesTab
